import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { observer } from 'mobx-react';
import { HOME, LOGIN_ADAL, MODULE_CHOICE, MODULE_PARAM_WORK_SITE } from './paths';
import { LinkRouteType } from './types';
import { ANONYMOUS_ROUTES, USER_ROUTES } from './routes';
import { ModuleProvider, useModuleContextSetter } from '00.app/module/moduleContext';
import { AppModulesIDs } from '30.quickConnect.Stores/RootStore/LoginStore/Payloads/responses';
import { useStore } from '30.quickConnect.Stores';

const RoutesBuilder = () => {
  // On récupère le store
  const {
    initQuickConnectApp,
    LoginStore: {
      connected,
      signInInfos: { userUPN, module },
      connectByMsal,
      availableModules,
    },
    DeclarationStore: { findBackUpsByUserUPNAsync },
    SessionStore: { setLastURLSaved },
  } = useStore();

  const location = useLocation();
  const setModule = useModuleContextSetter();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moduleWasChosen, setModuleWasChosen] = useState<boolean>(false);

  const isWorkSiteRoute = (path: string): boolean => path.startsWith(MODULE_PARAM_WORK_SITE);

  // On ajoute des hooks
  useEffect(() => {
    if (!connected && location.pathname !== HOME && location.pathname !== LOGIN_ADAL) {
      // Si on n'est pas connecté on renvoie vers le LOGIN
      if (pathname !== HOME) {
        setLastURLSaved(pathname + location.search);
        navigate(HOME);
      }
    }
  }, [connected, location.pathname, navigate, pathname, setLastURLSaved, location.search]);

  // Redirect to module choice login after msal auth if modules are available
  useEffect(() => {
    if (connected && connectByMsal && availableModules && Object.keys(availableModules).length > 1) {
      if (!moduleWasChosen) {
        navigate(MODULE_CHOICE);
        setModuleWasChosen(true);
        return;
      }
      if (moduleWasChosen && module) {
        setLastURLSaved('');
        navigate(HOME);
      }
    }
  }, [connectByMsal, connected, module, moduleWasChosen, navigate, setLastURLSaved, availableModules]);

  useEffect(() => {
    if (connected && module === AppModulesIDs.MODULE_PARAM_WORK_SITE_ID && !isWorkSiteRoute(pathname)) {
      const hasAccessToWebClient = availableModules?.[AppModulesIDs.MODULE_WEB_CLIENT_ID] !== undefined;

      if (hasAccessToWebClient) {
        setModule(AppModulesIDs.MODULE_WEB_CLIENT_ID, true);
        navigate(pathname, { replace: true });
      }
    }
  }, [connected, module, pathname, setModule, navigate, availableModules]);

  // NOTE: navigate a ete retire des dependances pour eviter de relancer
  // initQuickConnectApp a chaque changement de route (re-instanciation inutile des stores).
  useEffect(() => {
    (async () => {
      if (connected) {
        if (module === AppModulesIDs.MODULE_PARAM_WORK_SITE_ID) {
          return;
        }
        await initQuickConnectApp();
        // TODO: Voir pour désactiver la rech. dans la sauvegarde auto dans le cas où on passe par le DeepLink
        await findBackUpsByUserUPNAsync(userUPN);
      }
    })();
  }, [connected, initQuickConnectApp, findBackUpsByUserUPNAsync, userUPN, module, connectByMsal, moduleWasChosen]);

  const routesBuilderMemo = useMemo<LinkRouteType[]>(() => (connected ? USER_ROUTES : ANONYMOUS_ROUTES), [connected]);
  const routesBuilder = useRoutes(routesBuilderMemo);
  return <ModuleProvider value={module}>{routesBuilder}</ModuleProvider>;
};

export default observer(RoutesBuilder);
